import { Injectable } from '@angular/core';
import * as moment from 'moment-timezone';

import {
	AccreditingBody,
	ApplicationExpense,
	ApplicationTotals,
	Course,
	CourseOfStudy,
	DegreeObjective,
	EducationPathwayMilestoneIcon,
	State,
	MilestoneStepType,
	LogoType,
	Program,
	Expense,
	ThinParticipant,
	Participant,
	Supervisor,
	MilestoneStepProgram,
	FilterOptions,
	ExpenseType,
	DocumentSource,
	DocumentTag,
	OcrValidatedStudentExtract,
	OcrValidatedCourseExtract,
	OcrValidatedExpenseExtract,
	OcrValidatedDirectBillExtract,
	OcrValidatedReimbursementExtract,
	DefaultDocuments,
	AutomatedDocumentConfigurationTableData,
	DocumentDefaultFeature,
	Application,
	CareerPathway,
	UsernameType,
	SavingsSubCategory,
	Savings,
	ClientAddressType,
	HighestLevelOfEducation,
	CurrentEnrollmentUnivProgram,
	AvailableCoachingSlot,
	LoanProductType,
	LiveChat,
	VirtualCreditCardOptionsInterface,
	ProgramFeatureTableData,
	ProgramFeatureReference,
	Province,
	Country,
	MilestoneIcons,
	ApplicationStatus,
	WorkType,
	ProgramType,
	User,
	EventLogSubject,
	CourseMethod,
	Grade,
	MicroApps,
	WorkflowAssignmentMapping,
	PriorEducationDegree,
	ProviderCategoryType,
	EventLog,
	VirtualCardExpirationOptionType,
	WorkflowSupervisorAssignee,
	WorkflowSupervisionUserTableData,
	CapLimitCalculation,
	WorkflowTimeOffTableData,
	WorkflowProcessorTimeOff,
	DeterminationType,
	ProgramExpenseType,
	ThinProgram,
	ProgramLoanFrequency,
	CourseOfStudyCategory,
	EducationalProvider,
	Currency,
	ApplicationComment,
	AppealReason,
	AppealApproverLevel,
	BenefitPeriodType,
	RuleException,
	StudentLoanType,
	BenefitPeriod,
	MicroClient,
	PaymentFileFields,
	UserType,
	PaymentBatchDefinition,
	SupervisorTable,
	AppealStatus,
	LoanFrequency,
	LoanServicer,
	RepaymentOwner,
	RefundMethod,
	RefundReason,
	ReturnedReason,
	RefundCorrectionReason,
	FlatApplication,
	ParticipantAllHistoryTable,
	DataShareProvider,
	PaymentConfigField,
	RetirementEnrollmentHistory,
	LoanRepaymentStatus,
	DataShareMarketPlaceSurvey,
	DataShareExplorationSurveyTable
} from '@edassist/api';
import {
	PROGRAM_FEATURE_CODES,
	ReviewApplicationsTableData,
	SavingsTableData,
	constants,
	EXPENSE_TYPES,
	DOCUMENT_TYPE_CODES,
	PAYMENTS_CONFIG_FIELDS,
	AppStatus
} from '@edassist/common';
import { EmoticonData } from '@edassist/shared';
import { InputDropdownOption } from '@edassist-ui/input-dropdown';
import { InputMultiSelectOption } from '@edassist-ui/input-multiselect';
import { ParticipantSearchTableData, IAParticipantSearchTableData, ExpenseCardData } from '@edassist-ui/models';
import { TimezonePipe } from '@edassist-ui/timezone';

import { StudentExtractEmoticonData } from '../components/student-extract/student-extract-emoticon-data.interface';
import { CourseExtractEmoticonData } from '../components/course-extract/course-extract-emoticon-data';
import { ExpensesExtractTableData } from '../components/expenses-extract/expenses-extract-table-data.interace';
import { DirectBillExtractEmoticonData } from '../components/direct-bill-extract/direct-bill-extract-emoticon-data.interface';
import { ReimbursementExtractData } from '../components/reimbursement-extract/reimbursement-extract-data.interface';
import { WorkflowListTableData } from '@internal-admin/app/modules/workflow/workflow-list-table-data.interface';
import { RelatableProgram } from '@internal-admin/app/modules/client-view/programs-dashboard/program-view/program-general/program-related-applications-card/payment-programs-list/relatable-program.interface';
import { CreateExpensesForm } from '@internal-admin/app/modules/client-view/programs-dashboard/program-view/payment-type-details/program-eligible-expense-types/create-expenses-modal/create-expenses-form';
import { CareerPathwayJobs } from '@internal-admin/app/modules/career-pathways/roles/career-pathway-jobs.interface';
import { CurrencyPipe, TitleCasePipe } from '@angular/common';
import { GenericAddress } from '@internal-admin/shared/components/generic-editable-address/generic-address.interface';

@Injectable({ providedIn: 'root' })
export class MappingService {
	private static titleCasePipe = new TitleCasePipe();

	static mapCourseToExpenseCardData(course: Course, programType: string, currencyType: string): Array<ExpenseCardData> {
		const courseTotals: ExpenseCardData = {
			title: 'Tuition',
			id: -1,
			expenseType: EXPENSE_TYPES.TUITION,
			percentagePayOut: course.percentagePayOut,
			requestedAmount: course.requestedAmount,
			approvedAmount: course.approvedAmount,
			repaymentOwed: course.repaymentOwed,
			initialRepaymentAmount: course.repaymentOwed,
			edAssistOverrideAmount: course.edAssistOverrideAmount,
			edAssistOverrideApprovedAmount: course.edAssistOverrideApprovedAmount,
			assumedPaidAmount: course.edAssistOverridePaidAmount,
			edAssistOverridePaidAmount: course.edAssistOverridePaidAmount,
			paidAmount: course.paidAmount,
			refundAmount: course.refundAmount,
			adjustedRepaymentOwed: course.adjustedRepaymentOwed,
			repayDue: course.adjustedRepaymentOwed,
			exempt: course.taxExempt,
			maintainSkills: course.maintainSkills,
			meetMinimumQuals: course.meetMinimumQuals,
			newCareerField: course.newCareerField,
			editRepayIcon: course.paidAmount || course.edAssistOverridePaidAmount ? 'times-circle' : 'plus-circle',
			isRepayEditing: false,
			currencyType
		};
		const mappedObjects = course.expenses.map((expense: Expense) => {
			const expenseTotals: ExpenseCardData = {
				id: expense.id,
				expenseType: EXPENSE_TYPES.COURSE_RELATED,
				title: expense.expenseType.description,
				percentagePayOut: expense.percentagePayOut,
				requestedAmount: expense.requestedAmount,
				approvedAmount: expense.approvedAmount,
				repaymentOwed: expense.repaymentOwed,
				initialRepaymentAmount: expense.repaymentOwed,
				edAssistOverrideAmount: expense.edAssistOverrideAmount,
				edAssistOverrideApprovedAmount: expense.edAssistOverrideApprovedAmount,
				assumedPaidAmount: expense.edAssistOverridePaidAmount,
				edAssistOverridePaidAmount: expense.edAssistOverridePaidAmount,
				paidAmount: expense.paidAmount,
				numberOfBooks: expense.numberOfBooks,
				refundAmount: expense.refundAmount,
				adjustedRepaymentOwed: expense.adjustedRepaymentOwed,
				repayDue: expense.adjustedRepaymentOwed,
				exempt: expense.taxExempt,
				maintainSkills: expense.maintainSkills,
				meetMinimumQuals: expense.meetMinimumQuals,
				newCareerField: expense.newCareerField,
				editRepayIcon: expense.paidAmount || expense.edAssistOverrideAmount ? 'times-cirlce' : 'plus-circle',
				isRepayEditing: false,
				currencyType
			};

			return expenseTotals;
		});
		if (programType === constants.PROGRAM_TYPE_CODE.BOOK_FEE) {
			return [...mappedObjects];
		}

		return [courseTotals, ...mappedObjects];
	}

	static mapCoursesToExpenseCardData(courses: Array<Course>, programType: string, currencyType: string): any {
		return courses.reduce((map, course) => {
			map[course.id] = MappingService.mapCourseToExpenseCardData(course, programType, currencyType);

			return map;
		}, {});
	}

	static mapExpenseToExpenseCardData(
		expenses: Array<ApplicationExpense>,
		currencyType: string
	): Array<ExpenseCardData> {
		return expenses.map((appExpense: ApplicationExpense) => ({
			id: appExpense.id,
			title: appExpense.expenseType.description,
			percentagePayOut: appExpense.percentagePayOut,
			requestedAmount: appExpense.requestedAmount,
			approvedAmount: appExpense.approvedAmount,
			edAssistOverrideAmount: appExpense.edAssistOverrideAmount,
			edAssistOverrideApprovedAmount: appExpense.edAssistOverrideApprovedAmount,
			assumedPaidAmount: appExpense.edAssistOverridePaidAmount,
			edAssistOverridePaidAmount: appExpense.edAssistOverridePaidAmount,
			paidAmount: appExpense.paidAmount,
			refundAmount: appExpense.refundAmount,
			numberOfBooks: appExpense.numberOfBooks,
			repaymentOwed: appExpense.repaymentOwed,
			initialRepaymentAmount: appExpense.repaymentOwed,
			adjustedRepaymentOwed: appExpense.adjustedRepaymentOwed,
			repayDue: appExpense.adjustedRepaymentOwed,
			exempt: appExpense.taxExempt,
			maintainSkills: appExpense.maintainSkills,
			meetMinimumQuals: appExpense.meetMinimumQuals,
			newCareerField: appExpense.newCareerField,
			expenseType: EXPENSE_TYPES.NON_COURSE_RELATED,
			editRepayIcon: appExpense.paidAmount || appExpense.edAssistOverridePaidAmount ? 'times-cirlce' : 'plus-circle',
			isRepayEditing: false,
			currencyType
		}));
	}

	static mapApplicationTotalsToExpenseCardData(
		appTotals: ApplicationTotals,
		currencyType: string
	): Array<ExpenseCardData> {
		const totals: ExpenseCardData = {
			requestedAmount: appTotals.totalRequested,
			approvedAmount: appTotals.totalOverrideApprovedAmount,
			paidAmount: appTotals.totalOverridePaidAmount,
			numberOfBooks: 0,
			initialRepaymentAmount: appTotals.totalInitialRepaymentOwed,
			repaymentOwed: appTotals.totalInitialRepaymentOwed,
			refundAmount: appTotals.totalRefund,
			repayDue: appTotals.totalRepaymentOwed,
			editRepayIcon: appTotals.totalPaid || appTotals.totalOverridePaidAmount ? 'times-cirlce' : 'plus-circle',
			isRepayEditing: false,
			currencyType
		};

		return [totals, ...new Array<ExpenseCardData>()];
	}

	static mapParticipantToParticipantSearchTable(
		participants: Array<ThinParticipant | Participant>
	): Array<ParticipantSearchTableData> {
		return participants.map((participant) => ({
			id: participant.id,
			active: participant.active,
			firstName: participant.user?.firstName,
			lastName: participant.user?.lastName,
			employeeId: participant.employeeId,
			userTypeDescription: participant.user?.userType.description,
			activeStatus: participant.active ? 'Active' : 'Inactive',
			employeeStatus: participant.employeeStatus,
			userTypeId: participant.user?.userType.id
		}));
	}

	static mapPartcipantToParticipantSearchTableForIA(
		participants: Array<ThinParticipant | Participant>
	): Array<IAParticipantSearchTableData> {
		return participants.map((participant) => ({
			id: participant.id,
			active: participant.active,
			firstName: participant.user?.firstName,
			lastName: participant.user?.lastName,
			employeeId: participant.employeeId,
			userTypeDescription: participant.user?.userType.description,
			activeStatus: participant.active ? 'Active' : 'Inactive',
			employeeStatus: participant.employeeStatus,
			userTypeId: participant.user?.userType.id,
			uniqueId: participant.uniqueId,
			clientName: participant.clientName,
			email: participant.actualPreferEmail
		}));
	}

	static mapDegreeObjectiveToInputDropdownOption(degreeObjective: DegreeObjective): InputDropdownOption {
		return {
			display: degreeObjective.degree,
			value: degreeObjective
		};
	}

	static mapCourseOfStudyToInputDropdownOption(courseOfStudy: CourseOfStudy): InputDropdownOption {
		return {
			display: courseOfStudy.courseOfStudy,
			value: courseOfStudy
		};
	}

	static mapHighestLevelOfEducationToInputDropdownOption(
		highestLevelOfEducation: HighestLevelOfEducation
	): InputDropdownOption {
		return {
			display: highestLevelOfEducation.display,
			value: highestLevelOfEducation
		};
	}

	static mapCurrentEnrollmentUnivProgramToInputDropdownOption(
		currentEnrollmentUnivProgram: CurrentEnrollmentUnivProgram
	): InputDropdownOption {
		return {
			display: currentEnrollmentUnivProgram.display,
			value: currentEnrollmentUnivProgram
		};
	}

	static mapPaymentFileFieldToInputDropdownOption(paymentFileField: PaymentFileFields): InputDropdownOption {
		return {
			display: paymentFileField.name,
			value: paymentFileField.paymentFileFieldId
		};
	}

	static mapPaymentSftpDestinationServerToInputDropdownOptions(
		paymentConfigFields: Array<PaymentConfigField>
	): Array<InputDropdownOption> {
		return paymentConfigFields
			.filter((configField) => configField.configType === PAYMENTS_CONFIG_FIELDS.SFTP_DESTINATION_SERVER)
			.map((configField) => MappingService.mapPaymentConfigFieldToInputDropdownOption(configField));
	}

	// Common for different types of config
	static mapPaymentConfigFieldToInputDropdownOption(paymentConfigField: PaymentConfigField): InputDropdownOption {
		return {
			display: paymentConfigField.configFieldDisplay,
			value: paymentConfigField.configFieldValue
		};
	}

	static mapPaymentReportTypeToInputDropdownOptions(
		paymentConfigFields: Array<PaymentConfigField>
	): Array<InputDropdownOption> {
		return paymentConfigFields
			.filter((configField) => configField.configType === PAYMENTS_CONFIG_FIELDS.PAYMENT_REPORT_TYPE)
			.map((configField) => MappingService.mapPaymentConfigFieldToInputDropdownOption(configField));
	}

	static mapPaymentParentReportsToInputDropdownOptions(
		paymentBatchDefintions: Array<PaymentBatchDefinition>
	): Array<InputDropdownOption> {
		return paymentBatchDefintions
			.filter((config) => !config.parentBatchDefinitionId)
			.map((config) => MappingService.mapPaymentParentReportToInputDropdownOption(config));
	}

	static mapPaymentParentReportToInputDropdownOption(paymentConfig: PaymentBatchDefinition): InputDropdownOption {
		return {
			display: paymentConfig.reportName,
			value: paymentConfig.batchDefinitionId
		};
	}

	static mapSessionSlotsToInputDropdownOption(sessionSlot: AvailableCoachingSlot): InputDropdownOption {
		return {
			display: `${sessionSlot.formattedStart}-${sessionSlot.formattedEnd}`,
			value: sessionSlot
		};
	}

	static mapSessionSlotsToInputDropdownOptions(sessionSlots: Array<AvailableCoachingSlot>): Array<InputDropdownOption> {
		const now = new Date();

		return sessionSlots
			.map((sessionSlot) => MappingService.mapSessionSlotsToInputDropdownOption(sessionSlot))
			.filter((sessionSlot) => now.getTime() < new Date(sessionSlot.value.scheduledStart).getTime());
	}

	static mapHighestLevelOfEducationToInputDropdownOptions(
		highestLevelOfEducations: Array<HighestLevelOfEducation>
	): Array<InputDropdownOption> {
		return highestLevelOfEducations.map((highestLevelOfEducation) =>
			MappingService.mapHighestLevelOfEducationToInputDropdownOption(highestLevelOfEducation)
		);
	}

	static mapCurrentEnrollmentUnivProgramToInputDropdownOptions(
		currentEnrollmentUnivPrograms: Array<CurrentEnrollmentUnivProgram>
	): Array<InputDropdownOption> {
		return currentEnrollmentUnivPrograms.map((currentEnrollmentUnivProgram) =>
			MappingService.mapCurrentEnrollmentUnivProgramToInputDropdownOption(currentEnrollmentUnivProgram)
		);
	}

	static mapPaymentFileFieldsToInputDropdownOptions(
		paymentFileFields: Array<PaymentFileFields>
	): Array<InputDropdownOption> {
		return paymentFileFields.map((paymentFileField) =>
			MappingService.mapPaymentFileFieldToInputDropdownOption(paymentFileField)
		);
	}

	static mapDegreeObjectivesToInputDropdownOptions(
		degreeObjectives: Array<DegreeObjective>
	): Array<InputDropdownOption> {
		return degreeObjectives.map((degreeObjective) =>
			MappingService.mapDegreeObjectiveToInputDropdownOption(degreeObjective)
		);
	}

	static mapCoursesOfStudyToInputDropdownOptions(coursesOfStudy: Array<CourseOfStudy>): Array<InputDropdownOption> {
		return coursesOfStudy.map((courseOfStudy) => MappingService.mapCourseOfStudyToInputDropdownOption(courseOfStudy));
	}

	static mapSelectedEducationProvidersToInputDropdownOptions(
		educationalProvider: Array<EducationalProvider>
	): Array<InputDropdownOption> {
		return educationalProvider.map((provider) => ({
			display: provider.name,
			value: provider
		}));
	}

	static mapClientsToInputDropdownOptions(clients: Array<MicroClient>): Array<InputDropdownOption> {
		return clients.map((client) => ({
			display: client.name,
			value: client
		}));
	}

	static mapClientsToFilterOptions(clients: Array<MicroClient>): Array<FilterOptions> {
		return clients.map((client) => ({
			display: client.name,
			value: client.id
		}));
	}

	static mapProxyUsersToFilterOptions(pusers: Array<Participant>): Array<FilterOptions> {
		return pusers.map((user) => ({
			display: `${user.user.firstName} ${user.user.lastName} (${user.employeeId}/${user.uniqueId})`,
			value: user
		}));
	}

	static mapProgramsToFilterOptions(programs: Array<Program>): Array<FilterOptions> {
		return programs.map((program) => ({
			display: program.name,
			value: program.id
		}));
	}

	static mapStatusToFilterOptions(statuses: Array<ApplicationStatus>): Array<FilterOptions> {
		return statuses.map((status) => ({
			display: status.status,
			value: status.id
		}));
	}

	static mapWorkTypeToFilterOptions(workTypes: Array<WorkType>): Array<FilterOptions> {
		return workTypes.map((workType) => ({
			display: workType.description,
			value: workType.id
		}));
	}

	static mapProgramTypeToFilterOptions(programTypes: Array<ProgramType>): Array<FilterOptions> {
		return programTypes.map((programType) => ({
			display: programType.type,
			value: programType.id
		}));
	}

	static mapProgramDropdownOptions(programs: Array<Program | ThinProgram>): Array<InputMultiSelectOption> {
		return programs.map((program) => ({
			display: program.name,
			value: program
		}));
	}

	static mapLoanCardNumberToFilterOptions(statuses: Array<number>): Array<FilterOptions> {
		return statuses.map((loanCardNumber) => ({
			display: loanCardNumber.toString(),
			value: loanCardNumber
		}));
	}

	static mapProgramTypeToInputDropdownOptions(programTypes: Array<ProgramType>): Array<InputDropdownOption> {
		return programTypes.map((programType) => ({
			display: programType.type,
			value: programType
		}));
	}

	static mapYearOptionsToInputDropdownOptions(years: Array<number>): Array<InputDropdownOption> {
		return years.map((y) => ({
			display: y.toString(),
			value: y
		}));
	}

	static mapEducationPathwayLogoDropdownOptions(
		milestoneIcons: Array<EducationPathwayMilestoneIcon>
	): Array<InputDropdownOption> {
		return milestoneIcons.map((milestoneIcon) => ({
			display: milestoneIcon.name,
			value: milestoneIcon
		}));
	}

	static mapProgramNameDropdownOptions(programs: Array<Program>): Array<InputMultiSelectOption> {
		return programs.map((program) => ({
			display: program.name,
			value: program.id
		}));
	}

	static mapProgramIdNameDropdownOptions(programs: Array<Program>): Array<InputMultiSelectOption> {
		return programs.map((program) => ({
			display: `${program.id} - ${program.name}`,
			value: program.id
		}));
	}

	static mapProgramTypeDropdownOptions(programTypes: Array<ProgramType>): Array<InputDropdownOption> {
		return programTypes.map((programType) => ({
			display: programType.type,
			value: programType.id
		}));
	}

	static mapProgramTypeIdDropdownOptions(programTypes: Array<ProgramType>): Array<InputDropdownOption> {
		return programTypes.map((programType) => ({
			display: `${programType.id} - ${programType.type}`,
			value: programType.id
		}));
	}

	static mapPriorEducationDegreeMultiSelectOptions(
		priorEducationDegrees: Array<PriorEducationDegree>
	): Array<InputMultiSelectOption> {
		return priorEducationDegrees.map((ped) => ({
			display: ped.degreeName,
			value: ped.id
		}));
	}

	static mapProviderCategoryTypeDropdownOptions(
		providerCategoryTypes: Array<ProviderCategoryType>
	): Array<InputDropdownOption> {
		return providerCategoryTypes.map((pct) => ({
			display: pct.providerCategoryTypeDesc,
			value: pct.id
		}));
	}

	static mapReviewApplicationsData(
		items: Array<Application>,
		isSelectableData: boolean
	): Array<ReviewApplicationsTableData> {
		return items.map((app) => ({
			id: app.id,
			number: app.number,
			name: `${app.firstName} ${app.lastName}`,
			totalRequested: app.totalRequested,
			status: app.applicationStatus.code === constants.BULK_TYPES.APPEAL ? 'Appeal' : 'Application',
			startDate: app.applicationDetail.sessionStartDate,
			employerProgram: app.programName,
			benefitPeriod: app.benefitPeriod.name,
			rowSelectable: isSelectableData ? app.applicationStatus.code !== constants.BULK_TYPES.APPEAL : false,
			isSelected: false,
			applicationTypeId: app.applicationTypeId,
			participantId: app.participantId,
			programCurrencyType: app.benefitPeriod.program.currencyType.type
		}));
	}

	static mapStepProgramDropdownOptions(stepPrograms: Array<MilestoneStepProgram>): Array<InputMultiSelectOption> {
		return stepPrograms.map((stepProgram) => ({
			display: stepProgram.program.name,
			value: stepProgram.program.id
		}));
	}

	static mapStepTypeDropdownOptions(stepTypes: Array<MilestoneStepType>): Array<InputDropdownOption> {
		return stepTypes.map((stepType) => MappingService.mapStepTypeDropdownOption(stepType));
	}

	static mapStepTypeDropdownOption(stepType: MilestoneStepType): InputDropdownOption {
		return {
			display: stepType.name,
			value: stepType
		};
	}

	static mapCareerPathwayProvidersOption(mappedProviderTypes: Array<any>): Array<InputDropdownOption> {
		return mappedProviderTypes.map((mappedProviderType) => ({
			display: mappedProviderType.name,
			value: mappedProviderType
		}));
	}

	static mapCareerPathwaySkillsOption(mappedProviderTypes: Array<any>): Array<InputDropdownOption> {
		return mappedProviderTypes.map((mappedSkillsType) => ({
			display: mappedSkillsType.name,
			value: mappedSkillsType
		}));
	}

	static mapCareerPathwayJobsOption(mappedJobTypes: Array<CareerPathwayJobs>): Array<InputDropdownOption> {
		return mappedJobTypes.map((mappedJobType) => ({
			display: `${mappedJobType.name} - ${mappedJobType.domain.name}`,
			value: mappedJobType
		}));
	}

	static mapClientNameToInputDropdownOptions(clientNames: Array<string>): Array<InputDropdownOption> {
		return clientNames.map((clientName) => ({
			display: clientName,
			value: clientName
		}));
	}

	static mapProgramTypeAndCodeToInputDropdownOptions(mappedProgramTypes: Array<any>): Array<InputDropdownOption> {
		return mappedProgramTypes.map((mappedProgramType) => ({
			display: mappedProgramType.programType,
			value: mappedProgramType.programTypeCode
		}));
	}

	static mapApplicationStatusAndIdToInputDropdownOptions(mappedStatuses: Array<any>): Array<InputDropdownOption> {
		return mappedStatuses.map((mappedStatus) => ({
			display: mappedStatus.applicationStatus,
			value: mappedStatus.applicationStatusId
		}));
	}

	static mapAppealStatusAndIdToInputDropdownOptions(mappedStatuses: Array<AppealStatus>): Array<InputDropdownOption> {
		return mappedStatuses.map((mappedStatus) => ({
			display: mappedStatus.status,
			value: mappedStatus.id
		}));
	}

	static mapLoanServicerNameAndIdToInputDropdownOptions(
		mappedServicers: Array<LoanServicer>
	): Array<InputDropdownOption> {
		return mappedServicers.map((mappedServicer) => ({
			display: mappedServicer.name,
			value: mappedServicer.id
		}));
	}

	static mapWorkTypeToInputDropdownOptions(mappedWorkTypes: Array<any>): Array<InputDropdownOption> {
		return mappedWorkTypes.map((mappedWorkType) => ({
			display: mappedWorkType.typeDescription,
			value: mappedWorkType.typeId
		}));
	}

	static mapAccreditingBodyToInputDropdownOptions(
		accreditingBodies: Array<AccreditingBody>
	): Array<InputDropdownOption> {
		return accreditingBodies.map((accreditingBody) => ({
			display: accreditingBody.name,
			value: accreditingBody.id
		}));
	}

	static mapAccreditingBodyToInputDropdownOptionsWithFullName(
		accreditingBodies: Array<AccreditingBody>
	): Array<InputDropdownOption> {
		return accreditingBodies.map((accreditingBody) => ({
			display: accreditingBody.displayName,
			value: accreditingBody.id
		}));
	}

	static mapStatesToInputDropdownOptions(states: Array<State>): Array<InputDropdownOption> {
		return states.map((state) => ({
			display: state.abbreviation,
			value: state.abbreviation
		}));
	}

	static mapProvincesToInputDropdownOptions(provinces: Array<Province>): Array<InputDropdownOption> {
		return provinces?.map((province) => ({
			display: province.provinceDescription,
			value: province.provinceCode
		}));
	}

	static mapCountriesToInputDropdownOptions(countries: Array<Country>): Array<InputDropdownOption> {
		return countries?.map((country) => ({
			display: country.name,
			value: country.code
		}));
	}

	static mapDocumentSourceToInputDropdownOptions(documentSources: Array<DocumentSource>): Array<InputDropdownOption> {
		return documentSources.map((documentSource) => ({
			display: documentSource.description,
			value: documentSource
		}));
	}

	static mapOrphanedDocumentToEmailAccountsToInputDropdownOptions(
		orphanedDocumentToEmails: Array<string>
	): Array<InputDropdownOption> {
		return orphanedDocumentToEmails.map((orphanedDocumentToEmail) => ({
			display: orphanedDocumentToEmail,
			value: orphanedDocumentToEmail
		}));
	}

	static mapParticipantToSupervisor(participant: Participant): Supervisor {
		return {
			approvalLevel: 0,
			approvalType: {
				id: 4,
				type: 'Bypass'
			},
			supervisor: participant
		};
	}

	static mapGenericFilterResponseToDropdownOptions(filterOptions: Array<FilterOptions>): Array<InputMultiSelectOption> {
		return filterOptions.map((option) => ({
			display: option.display,
			value: option.value
		}));
	}

	static mapBenefitFilterResponseToDropdownOptions(filterOptions: Array<FilterOptions>): Array<InputMultiSelectOption> {
		return filterOptions.map((option) => ({
			display: option.display,
			value: option
		}));
	}

	static mapDegreeDropdownOptions(filterOptions: Array<DegreeObjective>): Array<InputDropdownOption> {
		return filterOptions.map((option) => ({
			display: option.degree,
			value: option
		}));
	}

	static mapCourseOfstudyDropdownOptions(filterOptions: Array<CourseOfStudy>): Array<InputDropdownOption> {
		return filterOptions.map((option) => ({
			display: `${option.courseOfStudy} - ${option.id}`,
			value: option
		}));
	}

	static mapLogoDropdownOptions(logoTypes: Array<LogoType>): Array<InputDropdownOption> {
		return logoTypes.map((logoType) => MappingService.mapStepTypeDropdownOption(logoType));
	}

	static mapLogoDropdownOption(logoType: LogoType): InputDropdownOption {
		return {
			display: logoType.name,
			value: logoType
		};
	}

	static mapParticipantPayTypeDropdownOptions(payTypes: Array<string>): Array<InputDropdownOption> {
		return payTypes.map((payType) => ({
			display: payType,
			value: payType === constants.hardcodedDropdownOptions.participantPayTypeOptions[0] ? 'N' : 'Y'
		}));
	}

	static mapGenericYesNoDropdownOptions(options: Array<string>): Array<InputDropdownOption> {
		return options.map((opt) => ({
			display: opt,
			value: opt.split('')[0]
		}));
	}

	static mapParticipantStatusDropdownOptions(options: Array<string>): Array<InputDropdownOption> {
		return options.map((opt) => ({
			display: opt,
			value: opt === constants.hardcodedDropdownOptions.participantStatusOptions[0] ? true : false
		}));
	}

	static mapExpenseTypesToDropDownOptions(expenseTypes: Array<ExpenseType>): Array<InputDropdownOption> {
		return expenseTypes.map((expenseType) => ({
			display: expenseType.description,
			value: expenseType
		}));
	}

	static mapSavingsToDropDownOptions(savingsTypes: Array<SavingsSubCategory>): Array<InputDropdownOption> {
		return savingsTypes.map((savingsType) => ({
			display: savingsType.description,
			value: savingsType
		}));
	}

	static mapDegreeObjectivesToIdDropdownOptions(degreeObjectives: Array<DegreeObjective>): Array<InputDropdownOption> {
		return degreeObjectives.map((degreeObjective) => ({
			display: degreeObjective.degree,
			value: degreeObjective.id
		}));
	}

	static mapCoursesOfStudyToIdDropdownOptions(coursesOfStudy: Array<CourseOfStudy>): Array<InputDropdownOption> {
		return coursesOfStudy.map((courseOfStudy) => ({
			display: courseOfStudy.courseOfStudy,
			value: courseOfStudy.id
		}));
	}

	static mapBenefitPeriodTypeToInputDropdownOptions(
		benefitPeriodTypes: Array<BenefitPeriodType>
	): Array<InputDropdownOption> {
		return benefitPeriodTypes.map((benefitPeriodType) => ({
			display: benefitPeriodType.description,
			value: benefitPeriodType.description
		}));
	}

	static mapToEmoticonData(extractedValue: string, confidenceType: number, isCurrency?: boolean): EmoticonData {
		return new EmoticonData(confidenceType, extractedValue, isCurrency);
	}

	static mapStudentExtractToStudentEmoticonData(
		ocrValidatedStudentExtract: OcrValidatedStudentExtract
	): StudentExtractEmoticonData {
		return {
			participantName: MappingService.mapToEmoticonData(
				ocrValidatedStudentExtract.participantName,
				ocrValidatedStudentExtract?.studentConfidence?.participantName
			),
			studentId: MappingService.mapToEmoticonData(
				ocrValidatedStudentExtract.studentId,
				ocrValidatedStudentExtract?.studentConfidence?.studentId
			),
			educationProvider: MappingService.mapToEmoticonData(
				ocrValidatedStudentExtract.educationProvider,
				ocrValidatedStudentExtract?.studentConfidence?.educationProvider
			),
			degreeType: MappingService.mapToEmoticonData(
				ocrValidatedStudentExtract.degreeType,
				ocrValidatedStudentExtract?.studentConfidence?.degreeType
			),
			fieldOfStudy: MappingService.mapToEmoticonData(
				ocrValidatedStudentExtract.fieldOfStudy,
				ocrValidatedStudentExtract?.studentConfidence?.fieldOfStudy
			),
			externalPartner: MappingService.mapToEmoticonData(ocrValidatedStudentExtract.externalPartner, undefined),
			enrollmentStatus: MappingService.mapToEmoticonData(ocrValidatedStudentExtract.enrollmentStatus, undefined),
			actualGraduationDate: MappingService.mapToEmoticonData(
				ocrValidatedStudentExtract?.actualGraduationDate ?
					moment(ocrValidatedStudentExtract.actualGraduationDate).format('MMM DD, YYYY')
				:	undefined,
				undefined
			),
			estimatedGraduationDate: MappingService.mapToEmoticonData(
				ocrValidatedStudentExtract?.estimatedGraduationDate ?
					moment(ocrValidatedStudentExtract.estimatedGraduationDate).format('MMM DD, YYYY')
				:	undefined,
				undefined
			)
		};
	}

	static mapCourseExtractToCourseEmoticonData(
		ocrValidatedCourseExtract: Array<OcrValidatedCourseExtract>
	): Array<CourseExtractEmoticonData> {
		return ocrValidatedCourseExtract.map((ocrItem) => ({
			name: MappingService.mapToEmoticonData(ocrItem.courseName, ocrItem.courseConfidence?.courseName),
			number: MappingService.mapToEmoticonData(ocrItem.courseNumber, ocrItem.courseConfidence?.courseNumber),
			startDate: MappingService.mapToEmoticonData(ocrItem.startDate, ocrItem.courseConfidence?.startDate),
			endDate: MappingService.mapToEmoticonData(ocrItem.endDate, ocrItem.courseConfidence?.endDate),
			term: MappingService.mapToEmoticonData(ocrItem.courseTerm, ocrItem.courseConfidence?.courseTerm),
			credits: MappingService.mapToEmoticonData(ocrItem.creditHours, ocrItem.courseConfidence?.creditHours),
			grade: MappingService.mapCourseExtractGradeEmoticonData(ocrItem),
			ceu: MappingService.mapToEmoticonData(ocrItem.ceusEarned, ocrItem.courseConfidence?.ceusEarned)
		}));
	}

	static mapOcrValidatedDocumentExpensesToExpensesTableData(
		ocrValidatedExpenseExtract: Array<OcrValidatedExpenseExtract>
	): Array<ExpensesExtractTableData> {
		return ocrValidatedExpenseExtract.map((ocrItem) => ({
			description: ocrItem.description,
			relatedCourse: ocrItem.relatedCourse,
			date: ocrItem.date,
			amount: ocrItem.amount
		}));
	}

	static mapToReimbursementData(
		ocrValidatedReimbursementExtract: OcrValidatedReimbursementExtract
	): ReimbursementExtractData {
		return {
			proofOfDeferment: ocrValidatedReimbursementExtract?.proofOfDeferment,
			reimbursementExtractTableData: ocrValidatedReimbursementExtract?.reimbursementPaymentExtractList.map(
				(ocrItem) => ({
					methodOfPayment: ocrItem?.methodOfPayment,
					date: ocrItem?.dateOfPayment,
					amount: MappingService.mapToEmoticonData(
						ocrItem?.amountPaid,
						ocrItem.reimbursementPaymentConfidence?.amountPaid,
						true
					)
				})
			)
		};
	}

	static mapDirectBillExtractToDirectBillEmoticonData(
		ocrValidatedDirectBillExtract: OcrValidatedDirectBillExtract
	): DirectBillExtractEmoticonData {
		return {
			paymentDue: MappingService.mapToEmoticonData(
				ocrValidatedDirectBillExtract?.paymentDue,
				ocrValidatedDirectBillExtract?.directBillConfidence?.paymentDue,
				true
			),
			paymentRemitToAddress: MappingService.mapToEmoticonData(
				ocrValidatedDirectBillExtract?.paymentRemitToAddress.filter((item) => item).join('\n'),
				ocrValidatedDirectBillExtract?.directBillConfidence?.paymentRemitToAddress
			)
		};
	}

	static mapDocumentTagOptionsToInputDropdownOptions(tagOptions: Array<DocumentTag>): Array<InputDropdownOption> {
		return tagOptions.map((tag) => ({
			display: tag.code,
			value: tag
		}));
	}

	static mapDefaultDocumentsTypesToFilterOptions(defaultDocuments: Array<DefaultDocuments>): Array<FilterOptions> {
		return defaultDocuments.map((defaultDocument) => ({
			display: defaultDocument.documentName,
			value: defaultDocument.documentTypeCode
		}));
	}

	static mapCourseExtractGradeEmoticonData(ocrValidatedCourseExtract: OcrValidatedCourseExtract): EmoticonData {
		let gradeEmoticonData: EmoticonData = MappingService.mapToEmoticonData(
			ocrValidatedCourseExtract.finalCourseGrade,
			ocrValidatedCourseExtract.courseConfidence?.finalCourseGrade
		);
		for (const propertyName of Object.keys(ocrValidatedCourseExtract)) {
			switch (propertyName) {
				case constants.GRADE_FIELD_TYPES.FINAL_COURSE_GRADE:
				case constants.GRADE_FIELD_TYPES.PROOF_OF_COMPLETION:
				case constants.GRADE_FIELD_TYPES.EXAM_RESULTS:
				case constants.GRADE_FIELD_TYPES.PROOF_OF_ATTENDANCE:
				case constants.GRADE_FIELD_TYPES.PROOF_OF_MEMBERSHIP:
					if (ocrValidatedCourseExtract[propertyName]) {
						gradeEmoticonData = MappingService.mapToEmoticonData(
							ocrValidatedCourseExtract[propertyName],
							ocrValidatedCourseExtract.courseConfidence?.[propertyName]
						);

						return gradeEmoticonData;
					}
					break;
				default:
					break;
			}
		}

		return gradeEmoticonData;
	}
	static mapAutomatedDocumentConfigurationTableData(
		automatedDocumentConfigurationTableData: Array<AutomatedDocumentConfigurationTableData>
	): Array<AutomatedDocumentConfigurationTableData> {
		return automatedDocumentConfigurationTableData.map((program: AutomatedDocumentConfigurationTableData) => ({
			programId: program.programId,
			programName: program.programName,
			programTypeId: program.programTypeId,
			programTypeName: program.programTypeName,
			isEdit: false,
			bookReceipt: MappingService.checkDocType(program.defaultDocuments, DOCUMENT_TYPE_CODES.TBRT),
			grades: MappingService.checkDocType(program.defaultDocuments, DOCUMENT_TYPE_CODES.GRAD),
			invoice: MappingService.checkDocType(program.defaultDocuments, DOCUMENT_TYPE_CODES.INVC),
			proofOfPayment: MappingService.checkDocType(program.defaultDocuments, DOCUMENT_TYPE_CODES.PPMT),
			proofOfGrdOrCmpl: MappingService.checkDocType(program.defaultDocuments, DOCUMENT_TYPE_CODES.PGDC)
		}));
	}

	static checkDocType(defaultDocuments: Array<DocumentDefaultFeature>, documentType: string): boolean {
		return defaultDocuments.some((doc: DocumentDefaultFeature) => doc.documentName === documentType);
	}

	static mapCareerPathwaysToDropdownOptions(careerPathways: Array<CareerPathway>): Array<InputDropdownOption> {
		return careerPathways?.map((careerPathway) => ({
			display: careerPathway.name,
			value: careerPathway.id
		}));
	}

	static mapUsernameTypesToInputDropdownOption(usernameTypes: Array<UsernameType>): Array<InputDropdownOption> {
		return usernameTypes.map((option) => ({
			display: option.description,
			value: option.id
		}));
	}

	static mapUsersToInputDropdownOption(users: Array<User>): Array<InputDropdownOption> {
		return users.map((user) => ({
			display: `${user.firstName} ${user.lastName}`,
			value: { id: user.id, userType: user.userType }
		}));
	}

	static mapClientMailingAddressTypeToInputDropdownOption(
		defaultMailing: Array<ClientAddressType>
	): Array<InputDropdownOption> {
		return defaultMailing
			.filter((mailing) => mailing.mailing)
			.map((option) => ({
				display: option.description,
				value: option.id
			}));
	}

	static mapClientEmailAddressTypeToInputDropdownOption(
		defaultEmail: Array<ClientAddressType>
	): Array<InputDropdownOption> {
		return defaultEmail
			.filter((mailing) => mailing.email)
			.map((option) => ({
				display: option.description,
				value: option.id
			}))
			.sort((firstElement, secondElement) => {
				if (secondElement.display === constants.EMAIL_TYPES.OTHER) {
					return -1;
				}

				return firstElement.display.localeCompare(secondElement.display);
			});
	}

	static mapSavingsToSavingsTableData(
		savingsData: Array<Savings>,
		savingsCategories: Array<SavingsSubCategory>,
		timezonePipe: TimezonePipe,
		currencyType?: string
	): Array<SavingsTableData> {
		const relatedSavings = MappingService.mapSavingsRelatedIdToSavingsId(savingsData);

		return relatedSavings.map((savings) => ({
			id: savings.id,
			relatedSavingsId: savings.relatedSavingsId,
			savingAmount: savings.savingAmount,
			savingNote: savings.savingNote,
			savingSubCategoryId: savings.savingSubCategoryId,
			savingCategoryId: savings.savingCategoryId,
			createdByName: savings.createdByName,
			createdOn: `${timezonePipe.transform(savings?.dateCreated, 'America/Denver', 'MMM D, YYYY')}`,
			reason: savingsCategories.find((savingType) => savingType.id === savings.savingSubCategoryId)?.description,
			currencyType
		}));
	}

	static mapLoanProductTypeToInputDropdownOption(productTypes: Array<LoanProductType>): Array<InputDropdownOption> {
		return productTypes.map((option) => ({
			display: option.description,
			value: option.id
		}));
	}

	static mapVCCOptionsToInputDropdownOption(
		vccOptions: Array<VirtualCreditCardOptionsInterface>
	): Array<InputDropdownOption> {
		return vccOptions.map((option) => ({
			display: option.labelName,
			value: option.id
		}));
	}

	static mapCourseMethodsToInputDropdownOption(courseMethods: Array<CourseMethod>): Array<InputDropdownOption> {
		return courseMethods.map((option) => ({
			display: option.method,
			value: option
		}));
	}

	static mapCourseGradesToInputDropdownOption(grades: Array<Grade>): Array<InputDropdownOption> {
		return grades.map((option) => ({
			display: option.name,
			value: option
		}));
	}

	static mapLiveChatToInputDropdownOption(liveChatOptions: Array<LiveChat>): Array<InputDropdownOption> {
		return liveChatOptions.map((option) => ({
			display: option.description,
			value: option.liveChatButtonId
		}));
	}

	static mapEventLogSubjectDropDownOption(eventLogSubject: Array<EventLogSubject>): Array<InputDropdownOption> {
		return eventLogSubject.map((eventLog) => ({
			display: eventLog.name,
			value: eventLog
		}));
	}

	static mapMicroApplicationDropDownOption(microApp: Array<MicroApps>): Array<InputDropdownOption> {
		return microApp.map((appList) => ({
			display: appList.number.toString(),
			value: { id: appList.id, number: appList.number }
		}));
	}

	static mapAlertToMicroApplications(eventLog: Array<EventLog>): Array<EventLog> {
		return eventLog.map((alert) => {
			if (alert.application) {
				alert.application = {
					id: alert.application.id,
					number: alert.application.number
				};
			} else {
				alert.application = { id: undefined, number: undefined };
			}

			return alert;
		});
	}

	static mapProgramFeatureTableData(
		programFeatureTableDataList: Array<ProgramFeatureTableData>
	): Array<ProgramFeatureTableData> {
		return programFeatureTableDataList.map((programFeature: ProgramFeatureTableData) => ({
			programId: programFeature.programId,
			programName: programFeature.programName,
			programTypeId: programFeature.programTypeId,
			programTypeName: programFeature.programTypeName,
			isEdit: false,
			automatedCurrencyConversion: MappingService.checkProgramFeature(
				programFeature?.programFeaturesReferences,
				PROGRAM_FEATURE_CODES.AUTOMATED_CURRENCY_CONVERSION
			),
			hideIneligibleProviders: MappingService.checkProgramFeature(
				programFeature?.programFeaturesReferences,
				PROGRAM_FEATURE_CODES.HIDE_INELIGIBLE_PROVIDERS
			),
			autoCloseRepaymentApplications: MappingService.checkProgramFeature(
				programFeature?.programFeaturesReferences,
				PROGRAM_FEATURE_CODES.AUTO_CLOSE_REPAYMENT_APPLICATIONS
			),
			hideDependentAnnualOption: MappingService.checkProgramFeature(
				programFeature?.programFeaturesReferences,
				PROGRAM_FEATURE_CODES.HIDE_DEPENDENT_ANNUAL_OPTION
			),
			locWarning: MappingService.checkProgramFeature(
				programFeature?.programFeaturesReferences,
				PROGRAM_FEATURE_CODES.LOC_WARNING
			)
		}));
	}

	static checkProgramFeature(
		programFeaturesReferences: Array<ProgramFeatureReference>,
		programFeatureCode: string
	): boolean {
		return programFeaturesReferences?.some(
			(programFeatureReference: ProgramFeatureReference) =>
				programFeatureReference.programFeature.code === programFeatureCode
		);
	}

	static mapMilestoneIconsToDropdownOptions(milestoneIcons: Array<MilestoneIcons>): Array<InputDropdownOption> {
		return milestoneIcons.map((milestoneIcon) => ({
			display: milestoneIcon.name,
			value: milestoneIcon.id
		}));
	}

	static mapVCardExpirationOptionTypes(
		vCardExpirationOptionTypes: Array<VirtualCardExpirationOptionType>
	): Array<InputDropdownOption> {
		return vCardExpirationOptionTypes.map((vCardExpirationOption) => ({
			display: vCardExpirationOption.vCardExpirationOptionType,
			value: vCardExpirationOption.vCardExpirationOptionTypeId
		}));
	}

	static mapWorkflowProcessorToInputDropdownOption(
		workflowProcessors: Array<User>,
		processorGroupName: string,
		faIconClassName: string,
		includeSeparator: boolean
	): Array<InputMultiSelectOption> {
		return workflowProcessors.map((option, index, array) => ({
			display: `${option.firstName} ${option.lastName}`,
			value: option.id,
			faIconName: faIconClassName,
			groupName: processorGroupName,
			separator: index + 1 === array.length ? includeSeparator : false
		}));
	}

	static mapWorkflowAssignmentToWorkflowListTableData(
		workflowAssignmentMappings: Array<WorkflowAssignmentMapping>,
		clientMap: Map<number, MicroClient>
	): Array<WorkflowListTableData> {
		const workflowAssignmentMap: Map<string, Array<WorkflowAssignmentMapping>> = new Map<
			string,
			Array<WorkflowAssignmentMapping>
		>();
		workflowAssignmentMappings.forEach((workflowAssignmentMapping) => {
			if (clientMap.get(workflowAssignmentMapping.clientId)) {
				const key = `${workflowAssignmentMapping.clientId}|${workflowAssignmentMapping.program.id}|${workflowAssignmentMapping.type.id}`;
				const values: Array<WorkflowAssignmentMapping> =
					workflowAssignmentMap.get(key)?.length ? workflowAssignmentMap.get(key) : [];
				values.push(workflowAssignmentMapping);
				workflowAssignmentMap.set(key, values);
			}
		});
		const wfMapKeys = Array.from(workflowAssignmentMap.keys());
		const workflowListTableDatas: Array<WorkflowListTableData> = [];
		wfMapKeys.forEach((wfMapKey) => {
			workflowListTableDatas.push(
				MappingService.buildWorkflowAssignmentMapping(workflowAssignmentMap.get(wfMapKey), clientMap)
			);
		});

		return workflowListTableDatas;
	}

	static mapRelatableProgramToInputDropdown(programs: Array<RelatableProgram>): Array<InputDropdownOption> {
		return programs.map((program) => ({
			display: program.name,
			value: program.id
		}));
	}

	static mapUsersToSupervisionUserTableData(supervisionUsers: Array<User>): Array<WorkflowSupervisionUserTableData> {
		return supervisionUsers.map((supervisionUser) => ({
			firstName: supervisionUser.firstName,
			lastName: supervisionUser.lastName,
			id: supervisionUser.id,
			rowSelectable: true
		}));
	}

	static mapWorkflowProcessorTimeOffToWorkflowTimeOffTableData(
		workflowProcessorTimeOffs: Array<WorkflowProcessorTimeOff>
	): Array<WorkflowTimeOffTableData> {
		return workflowProcessorTimeOffs.map((workflowProcessorTimeOff) => ({
			processorDayOffId: workflowProcessorTimeOff.id,
			teamMember: `${workflowProcessorTimeOff.user.firstName} ${workflowProcessorTimeOff.user.lastName}`,
			dayOffBegin: workflowProcessorTimeOff.dayOffBegin,
			dayOffEnd: workflowProcessorTimeOff.dayOffEnd,
			rowSelectable: true
		}));
	}

	static mapCapLimitCalculationOptionsToInputDropdown(
		capLimitCalculationOptions: Array<CapLimitCalculation>
	): Array<InputDropdownOption> {
		return capLimitCalculationOptions.map((capLimitCalculationOption) => ({
			display: capLimitCalculationOption.description,
			value: capLimitCalculationOption.id
		}));
	}

	static mapWorkflowSupervisionUserToWorkflowSupervisorAssignee(
		supervisorId: number,
		workflowSupervisionUserTableData: WorkflowSupervisionUserTableData,
		isAddCoordinator: boolean
	): WorkflowSupervisorAssignee {
		return {
			supervisor: {
				id: isAddCoordinator ? supervisorId : workflowSupervisionUserTableData.id
			},
			processor: {
				id: isAddCoordinator ? workflowSupervisionUserTableData.id : supervisorId
			}
		};
	}

	static mapDeterminationTypesToInputDropdown(
		determinationTypes: Array<DeterminationType>,
		programTypeCode: string
	): Array<InputDropdownOption> {
		if (
			programTypeCode === constants.PROGRAM_TYPE_CODE.LOAN_DIRECT_PAY ||
			programTypeCode === constants.PROGRAM_TYPE_CODE.LOAN_REIMBURSEMENT
		) {
			return determinationTypes.map((determinationType) => ({
				display:
					determinationType.displayText === constants.paymentTypeOptions.determinationTypeDisplayText.paymentDate ?
						constants.paymentTypeOptions.determinationTypeDisplayText.paymentRequestDate
					:	determinationType.displayText,
				value: determinationType.value
			}));
		}

		return determinationTypes.map((determinationType) => ({
			display: determinationType.displayText,
			value: determinationType.value
		}));
	}

	static mapCreateNewExpenseTypesFormToProgramExpenseTypes(
		form: CreateExpensesForm,
		programId: number
	): Array<ProgramExpenseType> {
		return form.selectedExpenseTypes.map((expType) => ({
			percentagePayout: +form.percentagePayout,
			programId,
			id: undefined,
			expenseType: expType,
			appliesCapLimit: form.appliesCapLimit
		}));
	}

	static mapThinProgramToInputDropdownOption(users: Array<ThinProgram>): Array<InputDropdownOption> {
		return users.map((program) => ({
			display: program.name,
			value: program
		}));
	}

	static mapThinProgramIdToInputDropdownOption(programs: Array<ThinProgram>): Array<InputDropdownOption> {
		return programs.map((program) => ({
			display: program.name,
			value: program.id
		}));
	}

	static mapProgramFrequencyOptionToMultiSelectOption(
		frequencies: Array<ProgramLoanFrequency>
	): Array<InputMultiSelectOption> {
		return frequencies.map((frequency) => ({
			display: frequency.name,
			value: frequency.code
		}));
	}

	static mapRuleExceptionsToMultiSelectOption(rules: Array<RuleException>): Array<InputMultiSelectOption> {
		return rules.map((rule) => ({
			display: rule.ruleFriendlyName,
			value: rule.id
		}));
	}

	static mapLoanFrequencySelectionsToDisplayString(loanFrequencies: Array<ProgramLoanFrequency>): string {
		return loanFrequencies
			.slice(0, 3)
			.map((p) => p.name)
			.join(', ');
	}

	static mapProgramsToInputDropdownOption(program: Program): Array<InputDropdownOption> {
		return [program].map((option) => ({
			display: option.name,
			value: option
		}));
	}

	static mapProviderShareTypeToInputDropdownOption(providerShareType: number): InputDropdownOption {
		return providerShareType === constants.providerShareOptions.percentage.value ?
				constants.providerShareOptions.percentage
			:	constants.providerShareOptions.flat;
	}

	static mapPaymentTypeToInputDropdownOption(paymentType: number): InputDropdownOption {
		if (paymentType === constants.paymentTypeOptions.perCourse.value) {
			return constants.paymentTypeOptions.perCourse;
		}

		if (paymentType === constants.paymentTypeOptions.perUnit.value) {
			return constants.paymentTypeOptions.perUnit;
		}

		return constants.paymentTypeOptions.subscription;
	}

	static mapCourseOfStudyCategoryMultiSelectOptions(
		courseOfStudyCategories: Array<CourseOfStudyCategory>
	): Array<InputMultiSelectOption> {
		return courseOfStudyCategories.map((cosc) => ({
			display: cosc.cosCategory,
			value: cosc.courseOfStudyCategoryId
		}));
	}

	static mapAccreditingBodyTypeMultiSelectOptions(accreditingBodyTypes: Array<string>): Array<InputMultiSelectOption> {
		return accreditingBodyTypes.map((abt) => ({
			display: abt,
			value: abt
		}));
	}

	static mapSystemEventLogCategoryMultiSelectOptions(
		systemEventLogCategories: Array<string>
	): Array<InputMultiSelectOption> {
		return systemEventLogCategories.map((selc) => ({
			display: selc,
			value: selc
		}));
	}

	static mapSystemEventLogSubjectMultiSelectOptions(
		systemEventLogSubjects: Array<string>
	): Array<InputMultiSelectOption> {
		return systemEventLogSubjects.map((sels) => ({
			display: sels,
			value: sels
		}));
	}

	static mapCurrencyArrayToCurrencySymbol(currencies: Array<Currency>, currencyCode: string): Currency {
		return currencies.find((curr) => curr.currencyCode.toLowerCase() === currencyCode.toLowerCase());
	}

	static mapApplicationCommentOptionsToInputDropdownOptions(
		applicationCommentOptions: Array<ApplicationComment>
	): Array<InputDropdownOption> {
		return applicationCommentOptions.map((appComment) => ({
			display: appComment.enableApplicationCommentsName,
			value: appComment.enableApplicationCommentsId
		}));
	}

	static mapAppealReasonsMultiSelectOptions(appealReasons: Array<AppealReason>): Array<InputMultiSelectOption> {
		return appealReasons.map((appealReason) => ({
			display: appealReason.reason,
			value: appealReason.id
		}));
	}

	static mapMicroClientsToInputDropdownOptions(clients: Array<MicroClient>): Array<InputDropdownOption> {
		return clients.map((client) => ({
			display: client.name,
			value: client
		}));
	}

	static mapApproverLevelsDropDownOptions(approverLevels: Array<AppealApproverLevel>): Array<InputDropdownOption> {
		return approverLevels.map((approverLevel) => ({
			display: approverLevel.appealsApproverDescription,
			value: approverLevel.id
		}));
	}

	static mapCurrencyOptionsToInputDropdownOption(currencyOptions: Array<Currency>): Array<InputDropdownOption> {
		return currencyOptions.map((currencyOption) => ({
			display: currencyOption.currencyCode,
			value: currencyOption.currencyId
		}));
	}

	static mapBenefitPeriodToInputDropdownOptions(benefitPeriods: Array<BenefitPeriod>): Array<InputDropdownOption> {
		return benefitPeriods.map((benefitPeriod) => ({
			display: benefitPeriod.name,
			value: benefitPeriod.id
		}));
	}

	static mapBenefitPeriodToStartEndDateOptions(benefitPeriods: Array<BenefitPeriod>): Array<InputDropdownOption> {
		return benefitPeriods.map((benefitPeriod) => ({
			display:
				benefitPeriod.benefitPeriodType?.toLowerCase() !== constants.BENEFIT_PERIOD_TYPES.Lifetime.toLowerCase() ?
					`${benefitPeriod.name} (${moment(benefitPeriod.startDate).format('MM/DD/YYYY')} - ${moment(
						benefitPeriod.endDate
					)
						.tz('America/Denver')
						.format('MM/DD/YYYY')})`
				:	undefined,
			value:
				benefitPeriod.benefitPeriodType.toLowerCase() !== constants.BENEFIT_PERIOD_TYPES.Lifetime.toLowerCase() ?
					benefitPeriod.id
				:	undefined
		}));
	}

	static mapStudentLoanTypeToInputDropdownOption(studentLoanTypes: Array<StudentLoanType>): Array<InputDropdownOption> {
		return studentLoanTypes.map((loanType) => ({
			display: loanType.type,
			value: loanType.id
		}));
	}

	static mapStatesIdsToInputDropdownOptions(states: Array<State>): Array<InputDropdownOption> {
		return states.map((state) => ({
			display: state.abbreviation.concat(' - ', state.description),
			value: state.id
		}));
	}

	static mapLoanFrequencyToInputDropdownOption(loanFrequency: Array<LoanFrequency>): Array<InputDropdownOption> {
		return loanFrequency.map((loanFrequency) => ({
			display: loanFrequency.name,
			value: loanFrequency.code
		}));
	}

	static mapUserTypesToInputDropdownOptions(userTypes: Array<UserType>): Array<InputDropdownOption> {
		const userList = userTypes.filter((userType) => {
			return constants.participant.participantStatusCodes.includes(userType.id);
		});

		return userList.map((options) => ({
			display: options.description,
			value: options.id
		}));
	}

	private static mapSavingsRelatedIdToSavingsId(savingsData: Array<Savings>): Array<Savings> {
		return savingsData.map((item) => {
			if (savingsData.some((saving) => saving.relatedSavingsId === item.id)) {
				item.relatedSavingsId = item.id;
			}

			return item;
		});
	}

	static mapClientNameDropdownOptions(clients: Array<MicroClient>): Array<InputDropdownOption> {
		return clients
			.map((client) => ({
				display: client.name,
				value: client.id
			}))
			.sort((a, b) => (a.display > b.display ? 1 : -1));
	}

	static mapBatchNameDropdownOptions(configs: Array<PaymentBatchDefinition>): Array<InputDropdownOption> {
		return configs
			.map((config) => ({
				display: `${config.reportName} - ${config.active ? 'Active' : 'Inactive'}`,
				value: config.batchDefinitionId
			}))
			.sort((a, b) => (a.display > b.display ? 1 : -1));
	}

	static mapReportNameToInputDropdownOptions(reportNames: Array<string>): Array<InputDropdownOption> {
		return reportNames
			.map((reportName) => ({
				display: reportName,
				value: reportName
			}))
			.sort((a, b) => (a.display > b.display ? 1 : -1));
	}

	static mapBatchStatusToInputDropdownOptions(mappedBatchStatuses: Array<any>): Array<InputDropdownOption> {
		return mappedBatchStatuses.map((mappedBatchStatus) => ({
			display: mappedBatchStatus.status,
			value: mappedBatchStatus.statusCode
		}));
	}

	static mapSupervisorTableResponse(
		supervisorResponse: Array<Supervisor>,
		timezonePipe: TimezonePipe
	): Array<SupervisorTable> {
		return supervisorResponse.map((response) => ({
			name: `${response.firstName} ${response.lastName}`,
			employeeId: response.employeeId,
			uniqueId: response.uniqueId,
			approvalLevel: response.approvalLevel,
			status: response.status?.toLowerCase() === 'active',
			role: response.role,
			participantId: response.participantId,
			alternateReviewer:
				response.proxyReview ?
					`${response.proxyReview.proxyReviewer?.user?.lastName},
								${response.proxyReview.proxyReviewer?.user?.firstName}
								${timezonePipe.transform(response.proxyReview?.startDate, 'America/Denver', 'MM/DD/YYYY')} -
								${timezonePipe.transform(response.proxyReview?.endDate, 'America/Denver', 'MM/DD/YYYY')}`
				:	'-'
		}));
	}

	static createIdObjMap(objArray: Array<any>): Map<any, any> {
		const map: Map<any, any> = objArray.reduce((objMap, obj) => {
			objMap.set(obj.id, obj);

			return objMap;
		}, new Map<any, any>());

		return map;
	}

	static mapToInputDropDown(
		map: Map<any, any>,
		idField: string,
		displayField: string,
		options: Array<any>
	): Array<InputDropdownOption> {
		const mappedOptions = options
			.filter((item) => map.has(item[idField]))
			.map((item) => {
				return {
					display: map.get(item[idField])[displayField],
					value: item[idField]
				};
			});

		return mappedOptions;
	}

	static mapEducationProgramsDropdownOptions(degreeObjectives: Array<DegreeObjective>): Array<InputDropdownOption> {
		return degreeObjectives.map((degreeObjective) => ({
			display: degreeObjective.degree,
			value: degreeObjective
		}));
	}

	static mapFieldOfStudyDropdownOptions(coursesOfStudy: Array<CourseOfStudy>): Array<InputDropdownOption> {
		return coursesOfStudy.map((courseOfStudy) => ({
			display: courseOfStudy.courseOfStudy,
			value: courseOfStudy
		}));
	}

	static mapRepaymentOwnerDropdownOptions(repaymentOwners: Array<RepaymentOwner>): Array<InputDropdownOption> {
		return repaymentOwners.map((repaymentOwner) => ({
			display: repaymentOwner.owner,
			value: repaymentOwner.id
		}));
	}

	static mapRefundMethodDropdownOptions(refundMethods: Array<RefundMethod>): Array<InputDropdownOption> {
		return refundMethods.map((refundMethod) => ({
			display: refundMethod.method,
			value: refundMethod.id
		}));
	}

	static mapRefundReasonDropdownOptions(refundReasons: Array<RefundReason>): Array<InputDropdownOption> {
		return refundReasons.map((refundReason) => ({
			display: refundReason.reason,
			value: refundReason.id
		}));
	}

	static mapReturnedReasonsDropdownOptions(returnedReasons: Array<ReturnedReason>): Array<InputDropdownOption> {
		return returnedReasons.map((returnedReason) => ({
			display: returnedReason.reason,
			value: returnedReason.id
		}));
	}

	static mapRefundCorrectionReasonsDropdownOptions(
		refundCorrectionReasons: Array<RefundCorrectionReason>
	): Array<InputDropdownOption> {
		return refundCorrectionReasons.map((refundCorrectionReason) => ({
			display: refundCorrectionReason.reason,
			value: refundCorrectionReason.id
		}));
	}

	static mapThinParticipantToParticipant(participant: ThinParticipant): Participant {
		return {
			id: participant.id,
			active: participant.active,
			employeeId: participant.employeeId,
			employeeStatus: participant.employeeStatus,
			uniqueId: participant.uniqueId,
			user: participant.user,
			clientName: participant.clientName,
			actualPreferEmail: participant.actualPreferEmail,
			preferEmail: participant.preferEmail
		};
	}

	static mapLoanServicerDropdownOptions(mappedServicers: Array<LoanServicer>): Array<InputDropdownOption> {
		return mappedServicers.map((mappedServicer) => ({
			display: mappedServicer.name,
			value: mappedServicer
		}));
	}

	static mapParticipantAllHistoryTable(
		applicationData: Array<FlatApplication>,
		timezonePipe: TimezonePipe,
		currencyPipe: CurrencyPipe
	): Array<ParticipantAllHistoryTable> {
		return applicationData.map((application) => {
			return {
				applicationId: application.id,
				applicationNumber: application.number,
				appReqStatus: AppStatus.APP_STATUSES[application.applicationStatusId]?.title,
				benefitPeriodName: application.benefitPeriodName,
				checkNumber: application.checkNumber,
				determinationType: application.determinationType,
				dateRange: MappingService.setDateRange(
					application.benefitPeriodStartDate,
					application.benefitPeriodEndDate,
					timezonePipe
				),
				programName: application.programName,
				programTypeCode: application.programTypeCode,

				totalApprovedAmount: currencyPipe.transform(
					application.totalApproved ?? 0,
					application.currencyType || 'USD',
					'symbol',
					'1.2-2'
				),
				paymentDate:
					application.paymentTransactionDate ?
						timezonePipe.transform(application.paymentTransactionDate, 'America/Denver', 'MM/DD/YYYY')
					:	'-',
				paymentAmt: currencyPipe.transform(
					application.totalPaid ?? 0,
					application.currencyType || 'USD',
					'symbol',
					'1.2-2'
				),
				taxableAmt: currencyPipe.transform(
					application.taxableAmount ?? 0,
					application.currencyType || 'USD',
					'symbol',
					'1.2-2'
				),
				refundAmt: currencyPipe.transform(
					application.totalRefund ?? 0,
					application.currencyType || 'USD',
					'symbol',
					'1.2-2'
				),
				repayDue: currencyPipe.transform(
					application.totalRepaymentOwedAmount + application.nonCourseTotalRepaymentOwedAmount,
					application.currencyType || 'USD',
					'symbol',
					'1.2-2'
				),
				currencyType: application.currencyType,
				programId: application.programId,
				sessionStartDate: application.sessionStartDate,
				sessionEndDate: application.sessionEndDate,
				dateCreated: application.dateCreated,
				paymentDueDate: application.paymentDueDate
			};
		});
	}

	static mapDataShareProvider(dataShareProviders: Array<DataShareProvider>): Array<InputDropdownOption> {
		return dataShareProviders.map((provider) => ({
			display: provider.providerName,
			value: provider.id
		}));
	}

	static buildAddress(parts: (string | null | undefined)[]): string {
		return parts.filter(Boolean).join(' ');
	}

	static getGenericAddress(addressObject: GenericAddress): string {
		const addressParts = [
			addressObject.address1,
			addressObject.address2,
			addressObject.city,
			addressObject.state,
			addressObject.zip,
			addressObject.country
		];

		return this.buildAddress(addressParts);
	}

	static getAddress(provider: EducationalProvider): string {
		const addressParts = [
			provider.address1,
			provider.address2,
			provider.city,
			provider.state,
			provider.zip,
			provider.country
		];

		return this.buildAddress(addressParts);
	}

	static getPaymentAddress(provider: EducationalProvider): string {
		const paymentAddressParts = [
			provider.paymentAddress1,
			provider.paymentAddress2,
			provider.paymentCity,
			provider.paymentState,
			provider.paymentZip,
			provider.paymentCountry
		];

		return this.buildAddress(paymentAddressParts);
	}

	static mapParticipantTaxHistoryTable(
		applicationData: Array<Application>,
		timezonePipe: TimezonePipe,
		currencyPipe: CurrencyPipe
	): Array<ParticipantAllHistoryTable> {
		return applicationData.map((application) => {
			return {
				applicationId: application.id,
				applicationNumber: application.number,
				appReqStatus: application.applicationStatus?.status,
				benefitPeriodName: application.benefitPeriod?.name,
				determinationType: application.benefitPeriod?.program?.determinationDate,
				dateRange: MappingService.setDateRange(
					application.applicationDetail?.sessionStartDate,
					application.applicationDetail?.sessionEndDate,
					timezonePipe
				),
				programName: application.programName,
				programTypeCode: application.benefitPeriod?.program?.programType?.code,
				totalApprovedAmount: currencyPipe.transform(
					application.totalApproved ?? 0,
					application.benefitPeriod?.program?.currencyType?.type || 'USD',
					'symbol',
					'1.2-2'
				),
				paymentDate:
					application.dateModified ?
						timezonePipe.transform(application.dateModified, 'America/Denver', 'MM/DD/YYYY')
					:	'-',
				paymentAmt: currencyPipe.transform(
					application.totalPaid ?? 0,
					application.benefitPeriod?.program?.currencyType?.type || 'USD',
					'symbol',
					'1.2-2'
				),
				refundAmt: currencyPipe.transform(
					application.totalRefund ?? 0,
					application.benefitPeriod?.program?.currencyType?.type || 'USD',
					'symbol',
					'1.2-2'
				),
				currencyType: application.benefitPeriod?.program?.currencyType?.type
			};
		});
	}

	static mapStatesWithNamesToInputDropdownOptions(states: Array<State>): Array<InputDropdownOption> {
		return states.map((state) => ({
			display: `${state.abbreviation} - ${state.description}`,
			value: state.abbreviation
		}));
	}

	private static buildWorkflowAssignmentMapping(
		workflowAssignmentMappings: Array<WorkflowAssignmentMapping>,
		clientMap: Map<number, MicroClient>
	): WorkflowListTableData {
		const workflowListTableData: WorkflowListTableData = {
			queueAssignmentMappingId: workflowAssignmentMappings[0].id,
			clientId: workflowAssignmentMappings[0].clientId,
			clientName: clientMap.get(workflowAssignmentMappings[0].clientId)?.name,
			programId: workflowAssignmentMappings[0].program.id,
			programName: workflowAssignmentMappings[0].program.name,
			typeId: workflowAssignmentMappings[0].type.id,
			typeDescription: workflowAssignmentMappings[0].type.description,
			assigneeId: workflowAssignmentMappings[0].user ? workflowAssignmentMappings[0].user.userId : undefined,
			assignedUsers: MappingService.getWorkflowUsers(workflowAssignmentMappings),
			rowSelectable: true
		};

		return workflowListTableData;
	}

	private static readonly getWorkflowUsers = (workflowAssignmentMappings: Array<WorkflowAssignmentMapping>) =>
		workflowAssignmentMappings.map((wfAssignmentMapping) =>
			wfAssignmentMapping.user ? wfAssignmentMapping.user : undefined
		);

	private static setDateRange(startDate: number | Date, endDate: number | Date, timezonePipe: TimezonePipe): string {
		if (startDate && endDate) {
			return `${timezonePipe.transform(startDate, 'America/Denver', 'MM/DD/YYYY')} -
			${timezonePipe.transform(endDate, 'America/Denver', 'MM/DD/YYYY')}`;
		}

		return '-';
	}

	static mapRetirementEnrollmentHistoryTable(
		enrollmentData: Array<RetirementEnrollmentHistory>,
		timezonePipe: TimezonePipe,
		user: User
	): Array<RetirementEnrollmentHistory> {
		return enrollmentData.map((enrollment) => {
			if (enrollment.userSelectionDate) {
				enrollment.userSelectionDate = timezonePipe.transform(
					enrollment.userSelectionDate,
					constants.TIMEZONE,
					constants.DATE_FORMAT
				);
			}
			if (enrollment.userSelectionEffectiveDate) {
				enrollment.userSelectionEffectiveDate = timezonePipe.transform(
					enrollment.userSelectionEffectiveDate,
					constants.TIMEZONE,
					constants.DATE_FORMAT
				);
			}
			if (enrollment.enrollmentStatus) {
				enrollment.enrollmentStatus = this.titleCasePipe.transform(enrollment.enrollmentStatus);
			}
			if (user) {
				enrollment.firstName = user.firstName;
				enrollment.lastName = user.lastName;
				enrollment.employeeId = user.username;
			}

			return enrollment;
		});
	}

	static mapLoanRepaymentStatusesToInputDropdownOptions(
		loanRepaymentStatuses: Array<LoanRepaymentStatus>
	): Array<InputDropdownOption> {
		return loanRepaymentStatuses.map((status) => ({
			display: status.display,
			value: status.value,
			tooltip: status.tooltip
		}));
	}

	static mapParticipantMarketPlaceDataToTableData(
		marketplaceData: Array<DataShareMarketPlaceSurvey>,
		timezonePipe: TimezonePipe,
	): Array<DataShareExplorationSurveyTable> {
		return marketplaceData.map(data => ({
			name: data.programInterest?.providerName,
			state: data.programInterest?.providerState,
			program: data.programInterest?.providerProgramName,
			fieldOfStudy: data.programInterest?.fieldOfStudies?.map(fieldOfStudy => fieldOfStudy).join(', '),
			createdBy: data.programInterest?.createdBy,
			event: data.event === 'favorited' ?
				'Marked Favorite' :
				data.event === 'unfavorited' ?
				'Unmarked Favorite' :
				data.event === 'request_information' ?
				'Learn More' :
				data.event?.charAt(0).toUpperCase() + data.event?.slice(1),
			eventDate: timezonePipe.transform(data.eventDate, 'America/Denver', 'MMM DD, YYYY')
		}));
	};

	// tslint:disable-next-line:max-file-line-count
}
